/**
 * The Platform Minimum: the floor a coach's Minimum Price may not go under.
 * Pure TypeScript - no React imports.
 *
 * The value is a flat 10 in every currency by product decision — a real constraint in
 * USD, a negligible one in NGN. The table is keyed by currency from the start so that
 * adopting real per-currency floors later is a data edit rather than a refactor.
 *
 * The contract only enforces `@Min(0)`, so this floor is advisory and bypassable until
 * the backend adopts it. See `docs/adr/0002-pricing-slice-uniform-currency-rows.md`.
 */

const FLAT_PLATFORM_MINIMUM = 10;

/**
 * Platform Minimum per currency. Currencies absent from this table have no floor.
 */
export const PLATFORM_MINIMUM_BY_CURRENCY: Readonly<Record<string, number>> = {
  USD: FLAT_PLATFORM_MINIMUM,
  EUR: FLAT_PLATFORM_MINIMUM,
  GBP: FLAT_PLATFORM_MINIMUM,
  CAD: FLAT_PLATFORM_MINIMUM,
  AUD: FLAT_PLATFORM_MINIMUM,
  NGN: FLAT_PLATFORM_MINIMUM,
  GHS: FLAT_PLATFORM_MINIMUM,
  KES: FLAT_PLATFORM_MINIMUM,
  UGX: FLAT_PLATFORM_MINIMUM,
  TZS: FLAT_PLATFORM_MINIMUM,
  ZAR: FLAT_PLATFORM_MINIMUM,
};

/**
 * Finds the Platform Minimum for a currency.
 *
 * Fails open: a currency we hold no floor for returns `null` rather than blocking the
 * coach, leaving only the contract's own `@Min(0)`.
 *
 * @param currency - currency code, matched case-insensitively
 * @returns the floor, or `null` when the currency has none
 */
export function findPlatformMinimum(currency: string): number | null {
  const code = normalizeCurrencyCode(currency);
  const minimum = PLATFORM_MINIMUM_BY_CURRENCY[code];

  return minimum === undefined ? null : minimum;
}

/**
 * Uppercases and trims a currency code so the floor table is looked up consistently,
 * whatever casing a row arrived with.
 */
function normalizeCurrencyCode(currency: string): string {
  return currency.trim().toUpperCase();
}
